import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useVerifyToken } from '../lib/auth.js';
import { ApiError } from '../lib/api.js';

export function VerifyPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const verify = useVerifyToken();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = params.get('token');
    const next = params.get('next') ?? '/';
    if (!token) {
      setError('Missing token in URL.');
      return;
    }
    (async () => {
      try {
        await verify.mutateAsync(token);
        // Force the /me query to refetch with the just-set cookie and wait
        // for it to settle. Without this, navigate() can run before Layout's
        // useMe has the new session and the header briefly renders the
        // signed-out state -- enough to flake e2e expectations.
        await qc.refetchQueries({ queryKey: ['me'] });
        navigate(next, { replace: true });
      } catch (e) {
        if (e instanceof ApiError) setError(e.message || 'This link is invalid or expired.');
        else setError('Could not verify the link.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h1 className="text-xl font-semibold">Signing in...</h1>
      {error ? (
        <p data-testid="verify-error" className="mt-4 rounded bg-red-50 p-4 text-sm text-red-700">
          {error}
        </p>
      ) : (
        <p className="mt-2 text-sm text-slate-600">One moment.</p>
      )}
    </div>
  );
}
